import React from 'react';

// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/virtual';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/grid';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import { useLocation } from '@reach/router';
import { extractLanguage, getStoryblokAlt } from '../../helpers/common';

import {
  render,
  MARK_LINK,
  NODE_PARAGRAPH,
} from 'storyblok-rich-text-react-renderer';
import FancyTitle from '../fancy-title';
import LinkResolver from '../../helpers/link-resolver';

function getImageDimensionsFromUrl(url, targetWidth) {
  const match = url?.match(/\/(\d+)x(\d+)\//);

  if (!match) {
    return {
      width: targetWidth,
      height: targetWidth,
    };
  }

  const originalWidth = Number(match[1]);
  const originalHeight = Number(match[2]);

  if (!originalWidth || !originalHeight) {
    return {
      width: targetWidth,
      height: targetWidth,
    };
  }

  return {
    width: targetWidth,
    height: Math.round((targetWidth * originalHeight) / originalWidth),
  };
}

function getOptimizedStoryblokUrl(url, width) {
  if (!url || !url.includes('a.storyblok.com')) {
    return url;
  }

  return `${url}/m/${width}x0/filters:format(webp)`;
}

function getOptimizedStoryblokFormatUrl(url, width, format) {
  if (!url || !url.includes('a.storyblok.com')) {
    return url;
  }

  return `${url}/m/${width}x0/filters:format(${format})`;
}

export default function TestimonialCardsContent({ blok }) {
  const { testimonials } = blok;
  const imageClasses = '';
  const location = useLocation();
  const currentLanguage = extractLanguage(location.pathname);


  return (
    <div className="px-4 lg:px-16">
      <Swiper
        modules={[Pagination]}
        slidesPerView="auto"
        className="w-full testimonials-slider testimonial-content-slider"
        centeredSlides
        spaceBetween={20}
        initialSlide={0}
        loop
        pagination={{
          clickable: true,
          renderBullet(index, className) {
            return `<span class="${className}">${index + 1}</span>`;
          },
        }}
        breakpoints={{
          320: {
            slidesPerView: 1,
          },
          1280: {
            slidesPerView: 3,
            spaceBetween: 64,
            initialSlide: 0,
          },
        }}
      >
        {testimonials
          && testimonials.length !== 0
          && testimonials.map((item) => {
            const image = item.logo.filename;
            const profile = item.profile_image.filename;
            const stars = 'https://www.latori.com/static/a1a6d47117cabc90d41ad645d00b9cc0/3065b/5-stars.avif';
            const show_stars = item.show_stars;
            const logoDimensions = getImageDimensionsFromUrl(image, 150);
            const profileDimensions = getImageDimensionsFromUrl(profile, 60);
            const optimizedLogo = getOptimizedStoryblokUrl(image, 300);
            const optimizedProfile = getOptimizedStoryblokUrl(profile, 120);
            const optimizedLogoAvif = getOptimizedStoryblokFormatUrl(image, 300, 'avif');
            const optimizedProfileAvif = getOptimizedStoryblokFormatUrl(profile, 120, 'avif');
            return (
              <SwiperSlide key={item._uid} className="!h-auto">
                <div className="border-2 rounded border-latori-green-dark-dark p-6 lg:p-8 gap-2 lg:gap-4 flex flex-col justify-start items-center h-full">
                  {image && (
                    <picture>
                      <source type="image/avif" srcSet={optimizedLogoAvif} />
                      <img
                        src={optimizedLogo}
                        className={`${
                          image ? 'hidden dark:inline-block' : ''
                        } mb-5${imageClasses || ''}`}
                        alt={getStoryblokAlt(item.logo, (currentLanguage === 'en' ? 'Company logo' : 'Firmenlogo'))}
                        width={logoDimensions.width}
                        height={logoDimensions.height}
                        loading="lazy"
                        decoding="async"
                        // objectFit="contain"
                        style={{
                          maxWidth: '150px',
                        }}
                      />
                    </picture>
                  )}
                  {image && (
                    <picture>
                      <source type="image/avif" srcSet={optimizedLogoAvif} />
                      <img
                        src={optimizedLogo}
                        className="dark:hidden mb-5 max-h-[50px] object-contain"
                        // objectFit="contain"
                        alt={getStoryblokAlt(item.logo, (currentLanguage === 'en' ? 'Company logo' : 'Firmenlogo'))}
                        width={logoDimensions.width}
                        height={logoDimensions.height}
                        loading="lazy"
                        decoding="async"
                        style={{
                          maxWidth: '100%',
                        }}
                      />
                    </picture>
                  )}
                  <div className="flex justify-center items-center gap-4">
                    {profile && (
                      <div className="bg-black p-[1px] rounded-full">
                        <picture>
                          <source type="image/avif" srcSet={optimizedProfileAvif} />
                          <img
                            src={optimizedProfile}
                            alt={getStoryblokAlt(item.profile_image, (currentLanguage === 'en' ? 'Profile picture' : 'Profilbild'))}
                            // objectFit="contain"
                            className=" "
                            width={profileDimensions.width}
                            height={profileDimensions.height}
                            loading="lazy"
                            decoding="async"
                            style={{
                              maxWidth: '60px',
                            }}
                          />
                        </picture>
                      </div>
                    )}
                    <FancyTitle
                      tag="h3"
                      text={item.name}
                      fontSize="text-xl xl:text-2xl"
                      marginBottom="mb-0"
                      styleOverrides="fancy-title-testimonials"
                    />
                  </div>
                  {show_stars && stars && (
                    <div className="xl:mt-4">
                      <img
                        src={stars}
                        alt={currentLanguage === 'en' ? '5 out of 5 stars' : '5 von 5 Sternen'}
                        width="249"
                        height="49"
                        loading="lazy"
                        decoding="async"
                        className="w-auto h-[16px]"
                      />
                    </div>
                  )}
                  <div className="xl:mt-4">
                    {render(item.content, {
                      markResolvers: {
                        [MARK_LINK]: LinkResolver,
                      },
                      nodeResolvers: {
                        // eslint-disable-next-line react/no-unstable-nested-components
                        [NODE_PARAGRAPH]: (children) => (
                          <p className="">{children}</p>
                        ),
                      },
                    })}
                  </div>
                </div>
              </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
}
